import {
  TestTube, AlertTriangle, AlertOctagon, HelpCircle, CheckCircle, ThumbsUp,
  RepeatIcon, Clock, AlertCircle, ListFilter, Text, Hash, CalendarDays, FileText,
  ListIcon, CheckSquareIcon,
  ListChecks,
  List,
  Scale,
  CheckCircle2,
  XCircle,
  ClipboardList
} from 'lucide-react';
import Link from 'next/link';
import { Badge, type BadgeProps } from '@/components/ui/badge';
import { UserTypeBadge } from '@/components/UserTypeBadge';
import { cn, formatDate } from '@/lib/utils';
import { type DataSchemaProperty, DataType, type FieldDefinition } from '@/types';
import { useAppState } from './useAppState';
import { convertSchemaPropertyToFieldDefinition } from './schemaConverter';
import { reviewStatus, type UserFlattened } from '@/server/db/schema';

export const getFieldDefinitions: (providedSchema?: any) => Promise<Record<string, FieldDefinition>> = async (providedSchema) => {
  console.log('[getFieldDefinitions] Starting', {
    hasProvidedSchema: !!providedSchema,
  });

  const state = useAppState.getState();

  // Schema is already fetched in loadAgentData, so use cache to avoid redundant fetch
  let schema = providedSchema;
  if (!schema && state.currentWorkspace && state.currentAgent) {
    const { getQueryClient } = await import('@/components/QueryClientProvider');
    const queryClient = getQueryClient();
    const workspaceSlug = state.currentWorkspace.slug;
    const agentSlug = state.currentAgent.slug;
    try {
      schema = await queryClient.fetchQuery({
        queryKey: ["/api", workspaceSlug, agentSlug, "schema"],
        queryFn: async () => {
          const response = await fetch(`/api/${workspaceSlug}/${agentSlug}/schema`);
          if (!response.ok) throw new Error('Failed to fetch schema');
          return response.json();
        },
      });
    } catch (err) {
      console.error('[getFieldDefinitions] Failed to load schema from cache', err);
      schema = null;
    }
  }

  // Use workspace members from global state (single source of truth)
  const members = state.workspaceMembers || [];

  console.log('[getFieldDefinitions] Schema loaded', {
    hasSchema: !!schema,
    hasProperties: !!schema?.properties,
    propertyCount: schema?.properties ? Object.keys(schema.properties).length : 0,
    membersCount: members.length,
  });

  const fields: Record<string, FieldDefinition> = {};
  if (schema?.properties) {
    for (const [key, prop] of Object.entries<DataSchemaProperty>(schema.properties)) {
      fields[key] = convertSchemaPropertyToFieldDefinition(key, prop);
    }
  }

  console.log('[getFieldDefinitions] Fields converted', {
    fieldCount: Object.keys(fields).length,
  });

  // Transform workspace members to auth users format for backward compatibility
  const authUsers = members.map((member: any) => ({
    value: member.systemUserId,
    label: member.email
  }));

  // Note: selected_office and other agent-specific fields come from manifest dataSchema
  // and are merged in reloadFieldDefinitions() in useAppState.tsx

  // All systemUserSelect fields include "Unassigned" option by default
  const systemUserSelectFields = Object.entries(fields).filter(([_, f]) => f.type === 'systemUserSelect');
  console.log('[getFieldDefinitions] systemUserSelect fields found:', {
    count: systemUserSelectFields.length,
    fieldKeys: systemUserSelectFields.map(([k]) => k),
    authUsersCount: authUsers.length,
  });

  if (authUsers.length > 0) {
    for (const [key, field] of Object.entries(fields)) {
      if (field.type === 'systemUserSelect') {
        field.options = [
          { value: 'unassigned', label: 'Unassigned' },
          ...authUsers
        ];
        console.log(`[getFieldDefinitions] Populated ${key} with ${field.options.length} options`);
      }
    }
  } else {
    console.warn('[getFieldDefinitions] No authUsers available to populate systemUserSelect fields');
  }

  if (fields.testStatus) {
    fields.testStatus.render = (value, record: UserFlattened) => {
      if (record && record.testUser === false) {
        return (
          <Badge variant="success" className="whitespace-nowrap">
            <CheckCircle className="inline mr-2 h-4 w-4" />Live
          </Badge>
        );
      }

      if (!value) return null;

      const getStatusDetails = (status: string): { label: string, variant: BadgeProps['variant'], icon: React.ReactNode } => {
        switch (status) {
          case 'sandbox':
            return { label: 'Sandbox', variant: 'neutral', icon: <TestTube className="inline mr-2 h-4 w-4" /> };
          case 'error_severe':
            return { label: 'Severe Error', variant: 'destructive', icon: <AlertOctagon className="inline mr-2 h-4 w-4" /> };
          case 'error_moderate':
            return { label: 'Moderate Error', variant: 'warning', icon: <AlertTriangle className="inline mr-2 h-4 w-4" /> };
          case 'error_minor':
            return { label: 'Minor Error', variant: 'warning', icon: <HelpCircle className="inline mr-2 h-4 w-4" /> };
          case 'neutral':
            return { label: 'Neutral', variant: 'neutral', icon: <List className="inline mr-2 h-4 w-4" /> };
          case 'positive_example':
            return { label: 'Positive Example', variant: 'default', icon: <ThumbsUp className="inline mr-2 h-4 w-4" /> };
          case 'excellent_example':
            return { label: 'Excellent Example', variant: 'success', icon: <CheckCircle className="inline mr-2 h-4 w-4" /> };
          case 'successful_example':
            return { label: 'Training Example', variant: 'default', icon: <ThumbsUp className="inline mr-2 h-4 w-4" /> };
          default:
            return { label: value, variant: 'neutral', icon: <List className="inline mr-2 h-4 w-4" /> };
        }
      };

      const { label, variant, icon } = getStatusDetails(value);

      return (
        <Badge variant={variant} className="whitespace-nowrap">
          {icon}{label}
        </Badge>
      );
    };
  }

  if (fields.userType) {
    fields.userType.render = (value) => (value ? <UserTypeBadge type={value} /> : null);
  }

  if (fields.userType) {
    fields.userType.options = [
      { value: 'all', label: 'All User Types' },
      { value: 'live', label: 'Live', icon: <CheckCircle className="inline mr-2 h-4 w-4" /> },
      { value: 'sandbox', label: 'Sandbox', icon: <TestTube className="inline mr-2 h-4 w-4" /> },
      { value: 'discussion', label: 'Discussion', icon: <ListChecks className="inline mr-2 h-4 w-4" /> },
      { value: 'eval', label: 'Evaluation', icon: <CheckSquareIcon className="inline mr-2 h-4 w-4" /> },
      { value: 'rated', label: 'Rated', icon: <Scale className="inline mr-2 h-4 w-4" /> },
      { value: 'eval_run', label: 'Eval Run', icon: <ClipboardList className="inline mr-2 h-4 w-4" /> }
    ];
  }

  // Beyond what's in the enum
  if (fields.testStatus) {
    fields.testStatus.options = [
      { value: 'all', label: 'All Statuses' },
      { value: 'sandbox', label: 'Test Environment', icon: <TestTube className="inline mr-2 h-4 w-4" /> },
      { value: 'error_severe', label: 'Severe Error', icon: <AlertOctagon className="inline mr-2 h-4 w-4" /> },
      { value: 'error_moderate', label: 'Moderate Error', icon: <AlertTriangle className="inline mr-2 h-4 w-4" /> },
      { value: 'error_minor', label: 'Minor Error', icon: <HelpCircle className="inline mr-2 h-4 w-4" /> },
      { value: 'neutral', label: 'Neutral', icon: <List className="inline mr-2 h-4 w-4" /> },
      { value: 'positive_example', label: 'Positive Example', icon: <ThumbsUp className="inline mr-2 h-4 w-4" /> },
      { value: 'excellent_example', label: 'Excellent Example', icon: <CheckCircle className="inline mr-2 h-4 w-4" /> },
      { value: 'successful_example', label: 'Success', icon: <ThumbsUp className="inline mr-2 h-4 w-4" /> }
    ];
  }

  if (fields.status) {
    fields.status.options = [
      { value: 'all', label: 'All Statuses' },
      { value: 'active', label: 'Active' },
      { value: 'inactive', label: 'Inactive' },
      { value: 'unresponsive', label: 'Unresponsive' },
      { value: 'success', label: 'Success' },
      { value: 'error', label: 'Error' },
      { value: 'dnc', label: 'Do Not Contact' }
    ];
  }

  if (fields.reviewStatus) {
    fields.reviewStatus.options = [
      { value: 'all', label: 'All Review Statuses' },
      { value: 'unread', label: 'Unread' },
      { value: 'good', label: 'Good' },
      { value: 'bad', label: 'Bad' }
    ];
  }

  return fields;
};
